import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2, FiHome, FiUsers, FiGrid,
  FiClipboard, FiCalendar, FiMapPin, FiAlertCircle, FiClock,
  FiNavigation, FiPlus, FiTrash2, FiStar
} from 'react-icons/fi';
import { BsDoorClosed } from 'react-icons/bs';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { establishmentsAPI, roomsAPI, usersAPI, ordersAPI, workSchedulesAPI, commonSpacesAPI, incidentsAPI, loginHistoryAPI, establishmentAddressesAPI } from '../../services/api';
import { getRoleName } from '../../utils/helpers';
import { USER_ROLES } from '../../utils/constants';
import RoomTable from '../../components/rooms/RoomTable';
import UserTable from '../../components/users/UserTable';
import CommonSpaceTable from '../../components/common_spaces/CommonSpaceTable';
import IncidentTable from '../../components/incidents/IncidentTable';
import EstablishmentForm from '../../components/establishments/EstablishmentForm';
import ScheduleCalendar from '../../components/schedules/ScheduleCalendar';
import CleaningOrdersTable from '../../components/orders/CleaningOrdersTable';
import LoginHistoryTable from '../../components/login_history/LoginHistoryTable';
import LoginHistoryDialog from '../../components/users/LoginHistoryDialog';
import UserForm from '../../components/users/UserForm';
import ChangePasswordDialog from '../../components/users/ChangePasswordDialog';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const EstablishmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useApp();

  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [establishment, setEstablishment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);

  // Data states
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [commonSpaces, setCommonSpaces] = useState([]);
  const [incidents, setIncidents] = useState([]);

  // Loading states
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingSpaces, setIsLoadingSpaces] = useState(false);
  const [isLoadingIncidents, setIsLoadingIncidents] = useState(false);

  // Filters for orders tab
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Load establishment data
  useEffect(() => {
    loadEstablishment();
  }, [id]);

  // Load tab-specific data when tab changes
  useEffect(() => {
    if (!establishment) return;

    switch (activeTab) {
      case 'rooms':
        loadRooms();
        break;
      case 'users':
        loadUsers();
        break;
      case 'spaces':
        loadCommonSpaces();
        break;
      case 'incidents':
        loadIncidents();
        break;
      default:
        break;
    }
  }, [activeTab, establishment]);

  const loadEstablishment = async () => {
    try {
      setIsLoading(true);
      const data = await establishmentsAPI.getById(id);
      setEstablishment(data);
    } catch (error) {
      console.error('Error loading establishment:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar la información del establecimiento'
      });
      navigate('/admin/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRooms = async () => {
    try {
      setIsLoadingRooms(true);
      const data = await roomsAPI.getAll({ establishment_id: id });
      setRooms(data);
    } catch (error) {
      console.error('Error loading rooms:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar las habitaciones'
      });
    } finally {
      setIsLoadingRooms(false);
    }
  };

  const loadUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const data = await usersAPI.getAll({ establishment_id: id });
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los usuarios'
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadCommonSpaces = async () => {
    try {
      setIsLoadingSpaces(true);
      const data = await commonSpacesAPI.getActiveByEstablishment(id);
      setCommonSpaces(data);
    } catch (error) {
      console.error('Error loading common spaces:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los espacios comunes'
      });
    } finally {
      setIsLoadingSpaces(false);
    }
  };

  const loadIncidents = async () => {
    try {
      setIsLoadingIncidents(true);
      const data = await incidentsAPI.getAll({ establishment_id: id });
      setIncidents(data);
    } catch (error) {
      console.error('Error loading incidents:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar las incidencias'
      });
    } finally {
      setIsLoadingIncidents(false);
    }
  };

  const handleUpdateEstablishment = async (formData) => {
    try {
      const updated = await establishmentsAPI.update(id, formData);
      setEstablishment(updated);
      setShowEditForm(false);
      addNotification({
        type: 'success',
        message: 'Establecimiento actualizado exitosamente'
      });
    } catch (error) {
      console.error('Error updating establishment:', error);
      addNotification({
        type: 'error',
        message: 'Error al actualizar el establecimiento'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!establishment) {
    return null;
  }

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: FiHome },
    { id: 'rooms', label: 'Habitaciones', icon: BsDoorClosed },
    { id: 'users', label: 'Usuarios', icon: FiUsers },
    { id: 'orders', label: 'Órdenes', icon: FiClipboard },
    { id: 'schedules', label: 'Cuadrantes', icon: FiCalendar },
    { id: 'spaces', label: 'Espacios Comunes', icon: FiMapPin },
    { id: 'incidents', label: 'Incidencias', icon: FiAlertCircle },
    { id: 'addresses', label: 'Direcciones', icon: FiNavigation },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center justify-center p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {establishment.name}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {establishment.city && establishment.country
                ? `${establishment.city}, ${establishment.country}`
                : 'Sin ubicación'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowEditForm(true)}
          className="btn btn-primary flex items-center space-x-2"
        >
          <FiEdit2 className="h-4 w-4" />
          <span>Editar</span>
        </button>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        {activeTab === 'overview' && (
          <OverviewTab establishment={establishment} />
        )}

        {activeTab === 'rooms' && (
          <RoomsTab
            rooms={rooms}
            isLoading={isLoadingRooms}
            onReload={loadRooms}
          />
        )}

        {activeTab === 'users' && (
          <UsersTab
            users={users}
            isLoading={isLoadingUsers}
            onReload={loadUsers}
            establishmentId={id}
          />
        )}

        {activeTab === 'orders' && (
          <OrdersTab
            establishmentId={id}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
          />
        )}

        {activeTab === 'schedules' && (
          <SchedulesTab establishmentId={id} addNotification={addNotification} />
        )}

        {activeTab === 'spaces' && (
          <SpacesTab
            spaces={commonSpaces}
            isLoading={isLoadingSpaces}
            onReload={loadCommonSpaces}
          />
        )}

        {activeTab === 'incidents' && (
          <IncidentsTab
            incidents={incidents}
            isLoading={isLoadingIncidents}
            onReload={loadIncidents}
          />
        )}

        {activeTab === 'addresses' && (
          <AddressesTab
            establishmentId={id}
            addNotification={addNotification}
          />
        )}
      </div>

      {/* Edit Form Modal */}
      {showEditForm && (
        <EstablishmentForm
          establishment={establishment}
          onClose={() => setShowEditForm(false)}
          onSave={handleUpdateEstablishment}
        />
      )}
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ establishment }) => {
  const metrics = [
    {
      label: 'Habitaciones',
      value: establishment.rooms_count || 0,
      icon: BsDoorClosed,
      color: 'blue'
    },
    {
      label: 'Usuarios',
      value: establishment.users_count || 0,
      icon: FiUsers,
      color: 'green'
    },
    {
      label: 'Órdenes',
      value: establishment.orders_count || 0,
      icon: FiClipboard,
      color: 'purple'
    },
    {
      label: 'Incidencias',
      value: establishment.incidents_count || 0,
      icon: FiAlertCircle,
      color: 'orange'
    }
  ];

  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600' }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          const colors = colorClasses[metric.color];
          return (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md p-3 ${colors.bg}`}>
                  <Icon className={`h-6 w-6 ${colors.text}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                  <p className="text-2xl font-semibold text-gray-900">{metric.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Establishment Details */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Información del Establecimiento</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoField label="Nombre" value={establishment.name} />
          <InfoField label="Estado" value={establishment.is_active ? 'Activo' : 'Inactivo'} />
          <InfoField label="Dirección" value={establishment.address} />
          <InfoField label="Ciudad" value={establishment.city} />
          <InfoField label="País" value={establishment.country} />
          <InfoField label="Código Postal" value={establishment.postal_code} />
          <InfoField label="Teléfono" value={establishment.phone} />
          <InfoField label="Email" value={establishment.email} />
          <InfoField label="Sitio Web" value={establishment.website} />
        </div>
      </div>

      {/* Subscription Info */}
      {establishment.active_subscription && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Suscripción Activa</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InfoField label="Plan" value={establishment.active_subscription.plan} />
              <InfoField label="Estado" value={establishment.active_subscription.status} />
              <InfoField
                label="Fecha de vencimiento"
                value={
                  establishment.active_subscription.current_period_end
                    ? new Date(establishment.active_subscription.current_period_end).toLocaleDateString()
                    : 'N/A'
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className="mt-1 text-sm text-gray-900">{value || 'N/A'}</p>
  </div>
);

// Rooms Tab Component
const RoomsTab = ({ rooms, isLoading, onReload }) => {
  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Habitaciones ({rooms.length})
        </h3>
        <button onClick={onReload} className="btn btn-secondary text-sm">
          Actualizar
        </button>
      </div>
      {rooms.length > 0 ? (
        <RoomTable rooms={rooms} onEdit={() => {}} onDelete={() => {}} />
      ) : (
        <div className="text-center py-8">
          <BsDoorClosed className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay habitaciones registradas</p>
        </div>
      )}
    </div>
  );
};

// Users Tab Component
const UsersTab = ({ users, isLoading, onReload, establishmentId }) => {
  const [loginHistory, setLoginHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [establishments, setEstablishments] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const { addNotification } = useApp();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadLoginHistory();
    loadEstablishments();
  }, [establishmentId]);

  const loadLoginHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const data = await loginHistoryAPI.getByEstablishment(establishmentId, {
        limit: 50  // Limit to last 50 logins
      });
      setLoginHistory(data);
    } catch (error) {
      console.error('Error loading login history:', error);
      // Don't show error notification as this is not critical
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadEstablishments = async () => {
    try {
      const establishmentsData = await establishmentsAPI.getAll();
      setEstablishments(establishmentsData);
    } catch (error) {
      console.error('Error loading establishments:', error);
    }
  };

  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setShowUserForm(true);
  };

  const handleDeleteUser = (userToDelete) => {
    if (userToDelete.id === currentUser?.id) {
      addNotification({
        type: 'error',
        message: 'No puedes eliminar tu propia cuenta'
      });
      return;
    }
    setDeleteConfirm(userToDelete);
  };

  const confirmDeleteUser = async () => {
    try {
      await usersAPI.delete(deleteConfirm.id);
      addNotification({
        type: 'success',
        message: 'Usuario eliminado exitosamente'
      });
      setDeleteConfirm(null);
      onReload(); // Reload users after deletion
    } catch (error) {
      console.error('Error deleting user:', error);
      addNotification({
        type: 'error',
        message: 'Error al eliminar el usuario'
      });
    }
  };

  const handleChangePassword = (userToChange) => {
    setChangePasswordUser(userToChange);
  };

  const handlePasswordChanged = async (newPassword) => {
    try {
      await usersAPI.changePassword(changePasswordUser.id, newPassword);
      setChangePasswordUser(null);
      addNotification({
        type: 'success',
        message: 'Contraseña cambiada exitosamente'
      });
    } catch (error) {
      console.error('Error changing password:', error);
      addNotification({
        type: 'error',
        message: 'Error al cambiar la contraseña'
      });
    }
  };

  const handleUserSaved = async (userData) => {
    try {
      if (selectedUser) {
        // Update existing user
        await usersAPI.update(selectedUser.id, userData);
        addNotification({
          type: 'success',
          message: 'Usuario actualizado exitosamente'
        });
      } else {
        // Create new user
        await usersAPI.create(userData);
        addNotification({
          type: 'success',
          message: 'Usuario creado exitosamente'
        });
      }
      setShowUserForm(false);
      setSelectedUser(null);
      onReload(); // Reload users after save
    } catch (error) {
      console.error('Error saving user:', error);
      addNotification({
        type: 'error',
        message: 'Error al guardar el usuario'
      });
      throw error; // Re-throw to keep form open
    }
  };

  const handleViewLoginHistory = (user) => {
    setSelectedUserForHistory(user);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getAvailableRoles = () => {
    if (currentUser?.role === USER_ROLES.PLATFORM_ADMIN) {
      return [
        { value: USER_ROLES.PLATFORM_ADMIN, label: getRoleName(USER_ROLES.PLATFORM_ADMIN) },
        { value: USER_ROLES.ESTABLISHMENT_ADMIN, label: getRoleName(USER_ROLES.ESTABLISHMENT_ADMIN) },
        { value: USER_ROLES.MANAGER, label: getRoleName(USER_ROLES.MANAGER) },
        { value: USER_ROLES.RECEPTIONIST, label: getRoleName(USER_ROLES.RECEPTIONIST) },
        { value: USER_ROLES.CLEANING_STAFF, label: getRoleName(USER_ROLES.CLEANING_STAFF) },
      ];
    } else {
      return [
        { value: USER_ROLES.ESTABLISHMENT_ADMIN, label: getRoleName(USER_ROLES.ESTABLISHMENT_ADMIN) },
        { value: USER_ROLES.MANAGER, label: getRoleName(USER_ROLES.MANAGER) },
        { value: USER_ROLES.RECEPTIONIST, label: getRoleName(USER_ROLES.RECEPTIONIST) },
        { value: USER_ROLES.CLEANING_STAFF, label: getRoleName(USER_ROLES.CLEANING_STAFF) },
      ];
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const availableRoles = getAvailableRoles();

  return (
    <div className="p-6 space-y-8">
      {/* Users Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Usuarios ({users.length})
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setSelectedUser(null);
                setShowUserForm(true);
              }}
              className="btn btn-primary text-sm flex items-center space-x-1"
            >
              <FiUsers className="h-4 w-4" />
              <span>Nuevo Usuario</span>
            </button>
            <button onClick={onReload} className="btn btn-secondary text-sm">
              Actualizar
            </button>
          </div>
        </div>
        {users.length > 0 ? (
          <UserTable
            users={users}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            onChangePassword={handleChangePassword}
            onViewLoginHistory={handleViewLoginHistory}
            currentUserId={currentUser?.id}
            currentUserRole={currentUser?.role || "PLATFORM_ADMIN"}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ) : (
          <div className="text-center py-8">
            <FiUsers className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay usuarios registrados</p>
          </div>
        )}
      </div>

      {/* Login History Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Historial de Inicios de Sesión
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Últimos 50 inicios de sesión de todos los usuarios del establecimiento
            </p>
          </div>
          <button onClick={loadLoginHistory} className="btn btn-secondary text-sm">
            Actualizar
          </button>
        </div>
        <LoginHistoryTable
          loginHistory={loginHistory}
          isLoading={isLoadingHistory}
        />
      </div>

      {/* User Form Modal */}
      {showUserForm && (
        <UserForm
          user={selectedUser}
          availableRoles={availableRoles}
          availableEstablishments={establishments}
          currentUser={currentUser}
          onSave={handleUserSaved}
          onClose={() => {
            setShowUserForm(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Eliminar Usuario"
          message={`¿Estás seguro de que deseas eliminar al usuario "${deleteConfirm.name}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteUser}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {/* Change Password Dialog */}
      {changePasswordUser && (
        <ChangePasswordDialog
          user={changePasswordUser}
          onSave={handlePasswordChanged}
          onClose={() => setChangePasswordUser(null)}
        />
      )}

      {/* Login History Dialog */}
      {selectedUserForHistory && (
        <LoginHistoryDialog
          user={selectedUserForHistory}
          onClose={() => setSelectedUserForHistory(null)}
        />
      )}
    </div>
  );
};

// Orders Tab Component
const OrdersTab = ({ establishmentId, selectedDate, onDateChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [commonSpaces, setCommonSpaces] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cleaningStaff, setCleaningStaff] = useState([]);
  const [roomOrders, setRoomOrders] = useState({});

  useEffect(() => {
    loadData();
  }, [selectedDate, establishmentId]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load all data in parallel
      const [roomsData, spacesData, ordersData, allUsersData] = await Promise.all([
        roomsAPI.getAll({ establishment_id: establishmentId }),
        commonSpacesAPI.getActiveByEstablishment(establishmentId),
        ordersAPI.getAll({ establishment_id: establishmentId, date: selectedDate }),
        usersAPI.getAll({ establishment_id: establishmentId })
      ]);

      // Filter only cleaning staff (personal_limpieza)
      const cleaningStaffData = (allUsersData || []).filter(user =>
        user.role === 'personal_limpieza' || user.role === 'CLEANING_STAFF'
      );

      setRooms(roomsData || []);
      setCommonSpaces(spacesData || []);
      setOrders(ordersData || []);
      setCleaningStaff(cleaningStaffData);

      // Transform and build roomOrders map (orders grouped by room/space)
      const ordersMap = {};
      (ordersData || []).forEach(order => {
        // Transform backend fields to frontend fields for consistency
        const transformedOrder = {
          ...order,
          // Map backend field names to frontend field names
          assigned_staff_id: order.cleaning_staff_id,
          bed_disposition_id: order.bed_disposition_id,
          bed_disposition_name: order.bed_disposition?.name || order.bed_disposition_name,
          cleaning_type_id: order.cleaning_type_id,
          cleaning_type_name: order.cleaning_type_obj?.name || order.cleaning_type_name,
          // Map timestamps to boolean values for UI
          checkout_status: order.checkout_timestamp !== null,
          cleaning_complete: order.cleaning_timestamp !== null,
          // Map cleaning_timestamp to status for frontend consistency
          status: order.cleaning_timestamp !== null ? 'completed' : 'pending'
        };

        if (order.room_id) {
          const key = `room-${order.room_id}`;
          if (!ordersMap[key]) ordersMap[key] = [];
          ordersMap[key].push(transformedOrder);
        } else if (order.common_space_id) {
          const key = `space-${order.common_space_id}`;
          if (!ordersMap[key]) ordersMap[key] = [];
          ordersMap[key].push(transformedOrder);
        }
      });
      setRoomOrders(ordersMap);

    } catch (error) {
      console.error('Error loading orders data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (newDate) => {
    onDateChange(newDate);
  };

  const handlePreviousDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    onDateChange(date.toISOString().split('T')[0]);
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    onDateChange(date.toISOString().split('T')[0]);
  };

  // Dummy handlers for read-only mode
  const handleOrderChange = () => {};
  const handleOrderCreate = () => {};
  const handleOrderDelete = () => {};
  const handleSaveOrders = () => {};
  const handleCheckoutStatusChange = () => {};
  const handleCleaningStatusChange = () => {};

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <CleaningOrdersTable
        rooms={rooms}
        commonSpaces={commonSpaces}
        roomOrders={roomOrders}
        cleaningStaff={cleaningStaff}
        onOrderChange={handleOrderChange}
        onOrderCreate={handleOrderCreate}
        onOrderDelete={handleOrderDelete}
        hasChanges={false}
        onSave={handleSaveOrders}
        isSaving={false}
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        onPreviousDay={handlePreviousDay}
        onNextDay={handleNextDay}
        onCheckoutStatusChange={handleCheckoutStatusChange}
        onCleaningStatusChange={handleCleaningStatusChange}
        establishmentId={establishmentId}
      />
    </div>
  );
};

// Schedules Tab Component
const SchedulesTab = ({ establishmentId, addNotification }) => {
  const handleError = (message) => {
    addNotification({
      type: 'error',
      message
    });
  };

  return (
    <div className="p-6">
      <ScheduleCalendar
        establishmentId={establishmentId}
        readOnly={true}
        onError={handleError}
      />
    </div>
  );
};

// Spaces Tab Component
const SpacesTab = ({ spaces, isLoading, onReload }) => {
  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Espacios Comunes ({spaces.length})
        </h3>
        <button onClick={onReload} className="btn btn-secondary text-sm">
          Actualizar
        </button>
      </div>
      {spaces.length > 0 ? (
        <CommonSpaceTable commonSpaces={spaces} onEdit={() => {}} onDelete={() => {}} />
      ) : (
        <div className="text-center py-8">
          <FiMapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay espacios comunes registrados</p>
        </div>
      )}
    </div>
  );
};

// Incidents Tab Component
const IncidentsTab = ({ incidents, isLoading, onReload }) => {
  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Incidencias ({incidents.length})
        </h3>
        <button onClick={onReload} className="btn btn-secondary text-sm">
          Actualizar
        </button>
      </div>
      {incidents.length > 0 ? (
        <IncidentTable incidents={incidents} onClose={() => {}} onReopen={() => {}} />
      ) : (
        <div className="text-center py-8">
          <FiAlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay incidencias registradas</p>
        </div>
      )}
    </div>
  );
};

// Addresses Tab Component
const AddressesTab = ({ establishmentId, addNotification }) => {
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadAddresses();
  }, [establishmentId]);

  const loadAddresses = async () => {
    try {
      setIsLoading(true);
      const data = await establishmentAddressesAPI.getAll({ establishment_id: establishmentId });
      setAddresses(data);
    } catch (error) {
      console.error('Error loading addresses:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar las direcciones'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedAddress(null);
    setShowForm(true);
  };

  const handleEdit = (address) => {
    setSelectedAddress(address);
    setShowForm(true);
  };

  const handleDelete = (address) => {
    setDeleteConfirm(address);
  };

  const confirmDelete = async () => {
    try {
      await establishmentAddressesAPI.delete(deleteConfirm.id);
      addNotification({
        type: 'success',
        message: 'Dirección eliminada exitosamente'
      });
      setDeleteConfirm(null);
      loadAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      addNotification({
        type: 'error',
        message: 'Error al eliminar la dirección'
      });
    }
  };

  const handleSetPrimary = async (address) => {
    try {
      await establishmentAddressesAPI.setPrimary(address.id);
      addNotification({
        type: 'success',
        message: 'Dirección marcada como principal'
      });
      loadAddresses();
    } catch (error) {
      console.error('Error setting primary address:', error);
      addNotification({
        type: 'error',
        message: 'Error al marcar como principal'
      });
    }
  };

  const handleSaveAddress = async (formData) => {
    try {
      if (selectedAddress) {
        await establishmentAddressesAPI.update(selectedAddress.id, formData);
        addNotification({
          type: 'success',
          message: 'Dirección actualizada exitosamente'
        });
      } else {
        await establishmentAddressesAPI.create({
          ...formData,
          establishment_id: parseInt(establishmentId)
        });
        addNotification({
          type: 'success',
          message: 'Dirección creada exitosamente'
        });
      }
      setShowForm(false);
      setSelectedAddress(null);
      loadAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      addNotification({
        type: 'error',
        message: 'Error al guardar la dirección'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Direcciones ({addresses.length})
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={handleCreate}
            className="btn btn-primary text-sm flex items-center space-x-1"
          >
            <FiPlus className="h-4 w-4" />
            <span>Nueva Dirección</span>
          </button>
          <button onClick={loadAddresses} className="btn btn-secondary text-sm">
            Actualizar
          </button>
        </div>
      </div>

      {addresses.length > 0 ? (
        <div className="space-y-4">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`border rounded-lg p-4 ${
                address.is_primary ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-gray-900">{address.name}</h4>
                    {address.is_primary && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        <FiStar className="h-3 w-3 mr-1" />
                        Principal
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{address.address}</p>
                  {(address.city || address.state || address.postal_code) && (
                    <p className="text-sm text-gray-500">
                      {[address.city, address.state, address.postal_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {address.country && (
                    <p className="text-sm text-gray-500">{address.country}</p>
                  )}
                  {(address.latitude && address.longitude) && (
                    <p className="text-xs text-gray-400 mt-1">
                      Coordenadas: {address.latitude.toFixed(6)}, {address.longitude.toFixed(6)}
                    </p>
                  )}
                  {address.notes && (
                    <p className="text-sm text-gray-500 mt-2 italic">{address.notes}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {!address.is_primary && (
                    <button
                      onClick={() => handleSetPrimary(address)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Marcar como principal"
                    >
                      <FiStar className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(address)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(address)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <FiNavigation className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay direcciones registradas</p>
          <p className="text-sm text-gray-400 mt-1">
            Añade direcciones para localizar las ubicaciones del establecimiento
          </p>
        </div>
      )}

      {/* Address Form Modal */}
      {showForm && (
        <AddressFormDialog
          address={selectedAddress}
          onSave={handleSaveAddress}
          onClose={() => {
            setShowForm(false);
            setSelectedAddress(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Eliminar Dirección"
          message={`¿Estás seguro de que deseas eliminar la dirección "${deleteConfirm.name}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

// Address Form Dialog Component
const AddressFormDialog = ({ address, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: address?.name || '',
    address: address?.address || '',
    city: address?.city || '',
    state: address?.state || '',
    postal_code: address?.postal_code || '',
    country: address?.country || '',
    latitude: address?.latitude || '',
    longitude: address?.longitude || '',
    notes: address?.notes || '',
    is_primary: address?.is_primary || false
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    // Prepare data for API
    const dataToSave = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null
    };

    await onSave(dataToSave);
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {address ? 'Editar Dirección' : 'Nueva Dirección'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Ej: Sede Principal, Almacén, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder="Calle, número, piso..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ciudad
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provincia/Estado
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código Postal
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  País
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Coordenadas (opcional)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Latitud
                  </label>
                  <input
                    type="number"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    step="any"
                    placeholder="Ej: 40.416775"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Longitud
                  </label>
                  <input
                    type="number"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    step="any"
                    placeholder="Ej: -3.703790"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Información adicional sobre esta ubicación..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_primary"
                name="is_primary"
                checked={formData.is_primary}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_primary" className="ml-2 text-sm text-gray-700">
                Marcar como dirección principal
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? 'Guardando...' : address ? 'Guardar Cambios' : 'Crear Dirección'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EstablishmentDetail;
